import { useCallback, useRef, useState } from 'react';

import { logService } from '../services';

/**
 * Hook for testing speaker output by playing a test tone
 */
const useSpeakerTest = () => {
  const [isTesting, setIsTesting] = useState(false);
  const [signalLevel, setSignalLevel] = useState(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const testSpeaker = useCallback(
    async(deviceId: string) => {
      if (isTesting) {
        logService.addLog('Speaker test already in progress');
        return;
      }

      try {
        setIsTesting(true);
        logService.addLog(`Testing speaker: ${deviceId}`);

        // Create a simple test tone using Web Audio API
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        const audioContext = audioContextRef.current;

        // Try to set sinkId on AudioContext if supported (Chrome 110+)
        if ('setSinkId' in audioContext && deviceId !== 'default') {
          try {
            await (audioContext as unknown as { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId);
            logService.addLog(`AudioContext sinkId set to: ${deviceId}`);
          } catch (err) {
            logService.addLog(`setSinkId on AudioContext failed, will use fallback: ${err}`);
          }
        }

        // Create oscillator for test tone (800Hz beep)
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.1; // Fast response

        oscillator.connect(gainNode);
        gainNode.connect(analyser);

        // Check if we need to use HTMLAudioElement fallback
        const needsAudioElementFallback =
          !('setSinkId' in audioContext) && deviceId !== 'default';

        if (needsAudioElementFallback) {
          // Fallback: Use MediaStreamDestination + HTMLAudioElement
          const destination = audioContext.createMediaStreamDestination();
          analyser.connect(destination);

          if (!audioElementRef.current) {
            audioElementRef.current = new Audio();
          }

          const audio = audioElementRef.current;
          audio.srcObject = destination.stream;

          // Set the output device
          if (typeof audio.setSinkId === 'function') {
            try {
              await audio.setSinkId(deviceId);
              logService.addLog(`Audio routed via HTMLAudioElement to: ${deviceId}`);
            } catch (err) {
              logService.addLog(`HTMLAudioElement setSinkId failed: ${err}`);
              console.warn('setSinkId failed:', err);
            }
          } else {
            logService.addLog('setSinkId not supported, using default output');
          }

          // Play the audio element - THIS WAS MISSING!
          try {
            await audio.play();
            logService.addLog('Audio element playback started');
          } catch (err) {
            logService.addLog(`Audio play failed: ${err}`);
          }
        } else {
          // Direct playback via AudioContext (already routed to correct device)
          analyser.connect(audioContext.destination);
        }

        // Configure tone
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        // Fade in and out to avoid clicks
        const now = audioContext.currentTime;
        const duration = 0.5;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.1);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.4);
        gainNode.gain.linearRampToValueAtTime(0, now + duration);

        // Animate the signal level during playback
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const animateSignal = () => {
          analyser.getByteFrequencyData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const average = sum / dataArray.length;
          const level = Math.min(100, Math.round((average / 255) * 300));

          setSignalLevel(level);

          if (audioContext.currentTime < now + duration + 0.1) {
            animationFrameRef.current = requestAnimationFrame(animateSignal);
          } else {
            setSignalLevel(0);
          }
        };

        // Play the tone
        oscillator.start(now);
        oscillator.stop(now + duration);
        animateSignal();

        // Wait for the tone to finish
        await new Promise((resolve) => setTimeout(resolve, 600));

        // Cleanup audio element if used
        if (needsAudioElementFallback && audioElementRef.current) {
          audioElementRef.current.pause();
          audioElementRef.current.srcObject = null;
        }

        logService.addLog('Speaker test completed');
      } catch (error) {
        const message = `Error testing speaker: ${error}`;
        logService.addLog(message);
        console.error('Speaker test error:', error);
      } finally {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setSignalLevel(0);
        setIsTesting(false);
      }
    },
    [isTesting],
  );

  return {
    testSpeaker,
    isTesting,
    signalLevel,
  };
};

export default useSpeakerTest;
